import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import {
  Shield, TrendingUp, FileText, Users,
  ArrowLeft, CheckCircle, BarChart3, Clock,
  Lightbulb, Factory, Globe, ChartLine
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Investor = () => {
  const { t, language } = useLanguage();

  // Translated benefits data
  const benefits = [
    {
      icon: Shield,
      title: t('investor.benefits.finance.title'),
      description: t('investor.benefits.finance.description'),
    },
    {
      icon: TrendingUp,
      title: t('investor.benefits.returns.title'),
      description: t('investor.benefits.returns.description'),
    },
    {
      icon: Lightbulb,
      title: t('investor.benefits.guidance.title'),
      description: t('investor.benefits.guidance.description'),
    },
    {
      icon: Factory,
      title: t('investor.benefits.manufacturing.title'),
      description: t('investor.benefits.manufacturing.description'),
    },
  ];

  // Translated investment approach data
  const investmentApproach = [
    {
      icon: Lightbulb,
      step: "01",
      title: t('investor.approach.step1.title'),
      description: t('investor.approach.step1.description'),
    },
    {
      icon: FileText,
      step: "02",
      title: t('investor.approach.step2.title'),
      description: t('investor.approach.step2.description'),
    },
    {
      icon: ChartLine,
      step: "03",
      title: t('investor.approach.step3.title'),
      description: t('investor.approach.step3.description'),
    },
    {
      icon: Globe,
      step: "04",
      title: t('investor.approach.step4.title'),
      description: t('investor.approach.step4.description'),
    },
  ];

  // Translated investment fields data
  const investmentFields = [
    {
      icon: Lightbulb,
      title: t('investor.fields.research.title'),
      description: t('investor.fields.research.description'),
    },
    {
      icon: Factory,
      title: t('investor.fields.inventions.title'),
      description: t('investor.fields.inventions.description'),
    },
    {
      icon: ChartLine,
      title: t('investor.fields.manufacturing.title'),
      description: t('investor.fields.manufacturing.description'),
    },
    {
      icon: Globe,
      title: t('investor.fields.partnerships.title'),
      description: t('investor.fields.partnerships.description'),
    },
  ];

  // Translated FAQ data
  const faqs = [
    {
      question: t('investor.faq.questions.0.question'),
      answer: t('investor.faq.questions.0.answer'),
    },
    {
      question: t('investor.faq.questions.1.question'),
      answer: t('investor.faq.questions.1.answer'),
    },
    {
      question: t('investor.faq.questions.2.question'),
      answer: t('investor.faq.questions.2.answer'),
    },
    {
      question: t('investor.faq.questions.3.question'),
      answer: t('investor.faq.questions.3.answer'),
    },
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-sm font-semibold text-sidebar-primary uppercase tracking-wide">
                  للمستثمرين
                </span>
                <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-6">
                  ستثمر اليوم وغيّر وضعك المالي خلال 6 أشهر
                </h1>
                <p className="text-lg text-sidebar-foreground/70 leading-relaxed mb-8">
                  لا تضيع دقيقة واحدة..! حقق أرباحاً ثابتة ستغير وضعك!
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg" className="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 gap-2">
                    <Link to="/investor-register">
                      انضم كمستثمر
                      <ArrowLeft className="w-5 h-5" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-sidebar-border text-black hover:bg-sidebar-accent">
                    تحدث مع مستشار
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                  <BarChart3 className="w-10 h-10 text-sidebar-primary mx-auto mb-3" />
                  <span className="text-3xl font-bold block">25%+</span>
                  <span className="text-sm text-sidebar-foreground/60">متوسط العائد</span>
                </div>
                <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                  <Shield className="w-10 h-10 text-sidebar-primary mx-auto mb-3" />
                  <span className="text-3xl font-bold block">92%</span>
                  <span className="text-sm text-sidebar-foreground/60">نسبة النجاح</span>
                </div>
                <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                  <Users className="w-10 h-10 text-sidebar-primary mx-auto mb-3" />
                  <span className="text-3xl font-bold block">200+</span>
                  <span className="text-sm text-sidebar-foreground/60">مستثمر نشط</span>
                </div>
                <div className="bg-sidebar-accent rounded-2xl p-6 text-center">
                  <Clock className="w-10 h-10 text-sidebar-primary mx-auto mb-3" />
                  <span className="text-3xl font-bold block">6 أشهر</span>
                  <span className="text-sm text-sidebar-foreground/60">متوسط التمويل</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Strategic Investment */}
        <section className="py-12 sm:py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{t('investor.strategic.title')}</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto text-sm sm:text-base">
                {t('investor.strategic.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12 sm:mb-16">
              <div className="bg-card rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-card">
                <h3 className="font-bold text-lg mb-4">{t('investor.strategic.promotion.title')}</h3>
                <p className="text-muted-foreground text-sm sm:text-base">
                  {t('investor.strategic.promotion.description')}
                </p>
              </div>
              <div className="bg-card rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-card">
                <h3 className="font-bold text-lg mb-4">{t('investor.strategic.incubators.title')}</h3>
                <p className="text-muted-foreground text-sm sm:text-base">
                  {t('investor.strategic.incubators.description')}
                </p>
              </div>
              <div className="bg-card rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-card">
                <h3 className="font-bold text-lg mb-4">{t('investor.strategic.direct.title')}</h3>
                <p className="text-muted-foreground text-sm sm:text-base">
                  {t('investor.strategic.direct.description')}
                </p>
              </div>
            </div>

            <div className="bg-card rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 shadow-card max-w-4xl mx-auto">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">{t('investor.strategic.mechanisms.title')}</h3>
              <p className="text-muted-foreground text-center text-sm sm:text-base mb-6 sm:mb-8">
                {t('investor.strategic.mechanisms.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Multi-Dimensional Manufacturing */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{t('investor.manufacturing.title')}</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto text-sm sm:text-base">
                {t('investor.manufacturing.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Factory className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h3 className="font-bold text-base sm:text-lg mb-2">{t('investor.manufacturing.fields.manufacturing')}</h3>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h3 className="font-bold text-base sm:text-lg mb-2">{t('investor.manufacturing.fields.development')}</h3>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <ChartLine className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h3 className="font-bold text-base sm:text-lg mb-2">{t('investor.manufacturing.fields.sustainability')}</h3>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h3 className="font-bold text-base sm:text-lg mb-2">{t('investor.manufacturing.fields.planning')}</h3>
              </div>
            </div>
          </div>
        </section>

        {/* How to Invest */}
        <section className="py-12 sm:py-16 md:py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{t('investor.approach.title')}</h2>
              <p className="text-sidebar-foreground/70 max-w-2xl mx-auto text-sm sm:text-base">
                {t('investor.approach.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {investmentApproach.map((step) => (
                <div key={step.step} className="relative bg-sidebar-accent rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div className="absolute -top-3 right-4 text-4xl font-bold text-sidebar-primary/20">
                    {step.step}
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-sidebar-primary flex items-center justify-center mb-4">
                    <step.icon className="w-5 h-5 sm:w-6 sm:h-6 text-sidebar-primary-foreground" />
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-3">{step.title}</h3>
                  <p className="text-sidebar-foreground/70 text-xs sm:text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Investment Fields */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{t('investor.fields.title')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
                {t('investor.fields.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {investmentFields.map((field) => (
                <div key={field.title} className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-card text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-hero-gradient flex items-center justify-center mx-auto mb-4">
                    <field.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-foreground" />
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-3">{field.title}</h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">{field.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 sm:py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{t('investor.faq.title')}</h2>
              <p className="text-muted-foreground text-sm sm:text-base">{t('investor.faq.subtitle')}</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-soft">
                  <h3 className="font-bold text-base sm:text-lg mb-3 flex items-start gap-2 sm:gap-3">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground text-sm mr-7 sm:mr-9">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Investor;